import { marked } from "marked"

import {
  getBasenameFromPath,
  SANDBOX_FILE_PREFIX,
  stripSandboxPrefix,
  tryDecodeUriComponent,
} from "@/features/chat/components/inline-session-image-utils"

type SessionImageContext = {
  sessionId: string
  filenames?: string[]
  /** When set (saved artifact), rewrite refs to this document-content base
   * instead of /api/session-content/{sessionId}, so recipients can load them. */
  contentBaseUrl?: string
}

function resolveSessionImageUrl(src: string, sessionImageContext?: SessionImageContext): string {
  const sessionId = sessionImageContext?.sessionId
  const contentBaseUrl = sessionImageContext?.contentBaseUrl
  if (!sessionId && !contentBaseUrl) return src

  const decodedSrc = tryDecodeUriComponent(String(src || ""))
  if (!decodedSrc) return src

  // Never rewrite data URLs or external http(s) images.
  if (/^data:/i.test(decodedSrc) || /^https?:/i.test(decodedSrc)) return src
  if (
    decodedSrc.startsWith("/api/session-content/") ||
    decodedSrc.startsWith("/session-content/")
  ) {
    return src
  }

  const filenames = sessionImageContext.filenames ?? []
  const normalized = stripSandboxPrefix(decodedSrc)
  const sandboxBasename = getBasenameFromPath(decodedSrc)

  const directMatch = filenames.find((filename) => normalized.includes(filename))
  let candidateFilename: string | null = null

  if (directMatch) {
    candidateFilename = directMatch
  } else if (filenames.includes(sandboxBasename)) {
    candidateFilename = sandboxBasename
  } else if (decodedSrc.startsWith(SANDBOX_FILE_PREFIX) && sandboxBasename) {
    candidateFilename = sandboxBasename
  }

  if (!candidateFilename) return src

  // Saved artifacts serve images from document-scoped storage; live canvases from
  // the session. Recipients of a shared artifact can't read session-content.
  if (contentBaseUrl) {
    return `${contentBaseUrl}/${encodeURIComponent(candidateFilename)}`
  }
  return `/api/session-content/${sessionId}/${encodeURIComponent(candidateFilename)}`
}

function rewriteSessionImageUrls(
  markdownContent: string,
  sessionImageContext?: SessionImageContext
): string {
  if (!sessionImageContext?.sessionId && !sessionImageContext?.contentBaseUrl) {
    return markdownContent
  }

  let result = markdownContent

  // Rewrite markdown image syntax: ![alt](src "optional title")
  result = result.replace(/!\[([^\]]*)\]\((\S+?)(\s+"[^"]*")?\)/g, (...args: unknown[]) => {
    const match = String(args[0] ?? "")
    const alt = String(args[1] ?? "")
    const src = String(args[2] ?? "")
    const title = args[3] ? String(args[3]) : undefined
    const resolved = resolveSessionImageUrl(src, sessionImageContext)
    if (resolved === src) return match
    return `![${alt}](${resolved}${title ?? ""})`
  })

  // Rewrite raw HTML <img src="..."> (LLMs sometimes emit HTML directly)
  result = result.replace(
    /<img\b([^>]*?)\bsrc=(["'])([^"']+)\2([^>]*?)>/gi,
    (...args: unknown[]) => {
      const match = String(args[0] ?? "")
      const before = String(args[1] ?? "")
      const quote = String(args[2] ?? "")
      const src = String(args[3] ?? "")
      const after = String(args[4] ?? "")
      const resolved = resolveSessionImageUrl(src, sessionImageContext)
      if (resolved === src) return match
      return `<img${before}src=${quote}${resolved}${quote}${after}>`
    }
  )

  return result
}

function escapeHtmlAttribute(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\n/g, "&#10;")
}

function closeTaskListsToLevel(
  outputLines: string[],
  indentStack: number[],
  targetLevel: number
): void {
  while (indentStack.length > 1 && indentStack[indentStack.length - 1]! > targetLevel) {
    outputLines.push("</li></ul>")
    indentStack.pop()
  }
  if (indentStack.length > 0) {
    outputLines.push("</li>")
  }
}

function convertTaskListsToTiptapHtml(markdownContent: string): string {
  const lines = markdownContent.split("\n")
  const outputLines: string[] = []
  const indentStack: number[] = []

  for (const line of lines) {
    const taskMatch = line.match(/^(\s*)- \[([ xX])\] (.*)$/)
    if (!taskMatch) {
      while (indentStack.length > 0) {
        outputLines.push("</li></ul>")
        indentStack.pop()
      }
      outputLines.push(line)
      continue
    }

    const indentLevel = taskMatch[1].length
    const checked = taskMatch[2].toLowerCase() === "x"
    const content = taskMatch[3]

    if (indentStack.length === 0) {
      outputLines.push('<ul data-type="taskList">')
      indentStack.push(indentLevel)
    } else {
      const lastIndent = indentStack[indentStack.length - 1]!
      if (indentLevel > lastIndent) {
        outputLines.push('<ul data-type="taskList">')
        indentStack.push(indentLevel)
      } else {
        closeTaskListsToLevel(outputLines, indentStack, indentLevel)
      }
    }

    outputLines.push(`<li data-type="taskItem" data-checked="${checked}"><p>${content}</p>`)
  }

  while (indentStack.length > 0) {
    outputLines.push("</li></ul>")
    indentStack.pop()
  }

  return outputLines.join("\n")
}

function preprocessMarkdownForTiptap(
  markdownContent: string,
  sessionImageContext?: SessionImageContext
): string {
  if (!markdownContent) return ""

  const codeFenceBlocks: string[] = []
  let result = markdownContent.replace(/```[\s\S]*?```/g, (match) => {
    const index = codeFenceBlocks.length
    codeFenceBlocks.push(match)
    return `@@CANVAS_CODE_FENCE_${index}@@`
  })

  // Block math: $$...$$
  result = result.replace(/\$\$([\s\S]+?)\$\$/g, (_match, latex) => {
    const escapedLatex = escapeHtmlAttribute(String(latex ?? "").trim())
    return `<div class="math-block" data-latex="${escapedLatex}"></div>`
  })

  // Inline math: $...$
  result = result.replace(/\$(?!\$)([^$\n]+?)\$(?!\$)/g, (_match, latex) => {
    const escapedLatex = escapeHtmlAttribute(String(latex ?? "").trim())
    return `<span class="math-inline" data-latex="${escapedLatex}"></span>`
  })

  // Citations: [[...]]
  result = result.replace(/\[\[([^\]]+)\]\]/g, (_match, citation) => {
    const escapedCitation = escapeHtmlAttribute(String(citation ?? ""))
    return `<span data-canvas-citation="${escapedCitation}"></span>`
  })

  // Task lists: convert to TipTap-compatible HTML
  result = convertTaskListsToTiptapHtml(result)

  result = rewriteSessionImageUrls(result, sessionImageContext)

  result = result.replace(/@@CANVAS_CODE_FENCE_(\d+)@@/g, (match, index) => {
    const parsedIndex = Number(index)
    return Number.isFinite(parsedIndex) ? (codeFenceBlocks[parsedIndex] ?? match) : match
  })

  return result
}

function configureMarkedForCanvas() {
  marked.setOptions({
    gfm: true,
    breaks: false,
  })
}

export function markdownToTiptapHtml(
  markdownContent: string,
  sessionImageContext?: SessionImageContext
): string {
  if (!markdownContent) return ""
  configureMarkedForCanvas()
  const parsed = marked.parse(preprocessMarkdownForTiptap(markdownContent, sessionImageContext))
  return typeof parsed === "string" ? parsed : ""
}

export function markdownToTiptapHtmlInline(
  markdownContent: string,
  sessionImageContext?: SessionImageContext
): string {
  if (!markdownContent) return ""
  configureMarkedForCanvas()
  const parsed = marked.parseInline(
    preprocessMarkdownForTiptap(markdownContent, sessionImageContext)
  )
  return typeof parsed === "string" ? parsed : ""
}
